/**
 * Product PDF Generator
 * 
 * Generates a customizable PDF datasheet for products with configurable
 * data sources including metafields, variants, and product properties.
 * 
 * Layout based on A4 portrait format with header, product image with callouts,
 * and footer sections.
 */

class ProductPDFGenerator {
  constructor(button) {
    this.button = button;
    this.container = button.closest('.product-pdf-generator');
    this.loadingState = this.container.querySelector('[data-pdf-loading]');
    this.config = this.parseConfig(button.dataset.pdfConfig);
    
    this.init();
  }

  init() {
    this.button.addEventListener('click', () => this.generatePDF());
  }

  parseConfig(configString) {
    console.log('Raw config string:', configString);
    
    // Shopify auto-escapes the data attribute, so we need to unescape it first
    const unescapedConfig = configString.replace(/&amp;/g, '&');
    console.log('Unescaped config:', unescapedConfig);
    
    // Now parse as normal URL parameters
    const config = {};
    const parts = unescapedConfig.split('&');
    
    for (const part of parts) {
      const equalIndex = part.indexOf('=');
      if (equalIndex > 0) {
        const key = part.substring(0, equalIndex);
        const value = part.substring(equalIndex + 1);
        config[key] = value;
      }
    }
    
    console.log('Parsed config:', config);
    return config;
  }

  async generatePDF() {
    try {
      this.setLoading(true);
      
      // Fetch product data
      const productData = await this.fetchProductData();
      
      // Check if jsPDF is loaded
      if (typeof window.jspdf === 'undefined') {
        // Load jsPDF library dynamically
        await this.loadJsPDF();
      }
      
      // Generate PDF
      await this.createPDF(productData);
      
      this.setLoading(false);
      // Success message removed - PDF downloads automatically
    } catch (error) {
      console.error('PDF Generation Error:', error);
      this.setLoading(false);
      this.showMessage('Failed to generate PDF. Please try again.', 'error');
    }
  }

  async loadJsPDF() {
    return new Promise((resolve, reject) => {
      // Load jsPDF from CDN
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = () => {
        console.log('jsPDF loaded successfully');
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load jsPDF library'));
      document.head.appendChild(script);
    });
  }

  async fetchProductData() {
    const productId = this.config.product_id;
    const variantId = this.config.variant_id;
    
    console.log('PDF Config:', this.config);
    
    // Fetch product JSON — keep the locale prefix (e.g. /fr) so translated
    // content (title, etc.) is returned instead of the primary locale's
    const pathname = window.location.pathname;
    const productsIndex = pathname.indexOf('/products/');
    if (productsIndex === -1) {
      throw new Error('Could not determine product handle');
    }
    const localePrefix = pathname.substring(0, productsIndex);
    const productHandle = pathname.split('/products/')[1]?.split('?')[0];
    if (!productHandle) {
      throw new Error('Could not determine product handle');
    }

    const response = await fetch(`${localePrefix}/products/${productHandle}.js`);
    if (!response.ok) {
      throw new Error('Failed to fetch product data');
    }
    
    const product = await response.json();
    console.log('Product data fetched:', product.title);
    
    // Extract dynamic fields from configuration
    const customFields = [];
    for (let i = 1; i <= 5; i++) {
      const fieldSource = this.config[`field_${i}_source`];
      const fieldLabel = this.config[`field_${i}_label`];
      
      console.log(`Field ${i}:`, {
        source: fieldSource,
        label: fieldLabel,
        hasSource: !!fieldSource,
        hasLabel: !!fieldLabel
      });
      
      if (fieldSource && fieldLabel) {
        const fieldData = await this.getMetafieldData(productHandle, fieldSource);
        console.log(`Field ${i} data retrieved:`, fieldData ? `Found (${fieldData.length || 'N/A'} chars)` : 'No data');
        
        if (fieldData) {
          customFields.push({
            label: this.safeDecodeLabel(fieldLabel),
            data: fieldData,
            index: i
          });
        } else {
          console.warn(`Field ${i} (${fieldLabel}): metafield "${fieldSource}" has no data`);
        }
      } else {
        if (!fieldSource) console.log(`Field ${i}: No source configured`);
        if (!fieldLabel) console.log(`Field ${i}: No label configured`);
      }
    }
    
    // Get middle section content if configured
    let middleSectionContent = null;
    if (this.config.middle_section) {
      middleSectionContent = await this.getMetafieldData(productHandle, this.config.middle_section);
    }
    
    const productData = {
      name: this.getProductName(product),
      sku: this.getProductSKU(product, variantId),
      image: product.featured_image || (product.images && product.images[0]),
      middleSection: middleSectionContent,
      customFields: customFields,
      sizes: this.config.show_sizes === 'true' ? this.extractSizes(product) : null,
      product: product
    };
    
    console.log('Product data compiled:', productData);
    console.log('Has image:', !!productData.image);
    console.log('Has middleSection:', !!productData.middleSection);
    console.log(`Found ${customFields.length} custom fields to render`);
    if (middleSectionContent) {
      console.log('Middle section content found');
    }
    return productData;
  }

  getProductName(product) {
    if (this.config.name_source === 'title') {
      return product.title;
    }
    // For metafields, we'll fetch them separately
    return product.title;
  }

  getProductSKU(product, variantId) {
    // Check if product_sku from metafield is provided in config
    if (this.config.product_sku) {
      console.log('Using product metafield SKU:', this.config.product_sku);
      return this.config.product_sku;
    }
    
    // Fallback to variant SKU
    if (this.config.sku_source === 'variant_sku') {
      const variant = product.variants.find(v => v.id == variantId);
      return variant?.sku || product.variants[0]?.sku || 'N/A';
    }
    return product.variants[0]?.sku || 'N/A';
  }

  async getMetafieldData(productHandle, source) {
    if (!source) {
      console.log('No source provided for metafield');
      return null;
    }
    
    console.log(`Looking for metafield: ${source}`);
    
    // Example: If metafields are embedded in the page via liquid
    const metafieldElement = document.querySelector(`[data-metafield="${source}"]`);
    if (metafieldElement) {
      const content = metafieldElement.textContent.trim();
      console.log(`Found metafield ${source}:`, content.substring(0, 100) + '...');
      
      if (!content) {
        console.warn(`Metafield ${source} is empty`);
        return null;
      }
      
      try {
        const parsed = JSON.parse(content);
        
        // Check if it's a rich text metafield (Shopify rich text format)
        if (parsed && typeof parsed === 'object' && parsed.type === 'root' && parsed.children) {
          console.log(`Detected rich text metafield ${source}, extracting text...`);
          const extractedText = this.extractRichTextContent(parsed);
          console.log(`Extracted text from ${source}:`, extractedText);
          return extractedText;
        }
        
        console.log(`Parsed metafield ${source} as JSON:`, parsed);
        return parsed;
      } catch {
        console.log(`Using metafield ${source} as text:`, content);
        return content;
      }
    }
    
    console.warn(`Metafield element not found for: ${source}`);
    
    // List all available metafields for debugging
    const allMetafields = document.querySelectorAll('[data-metafield]');
    console.log('Available metafields:', Array.from(allMetafields).map(el => el.getAttribute('data-metafield')));
    
    return null;
  }

  extractRichTextContent(richTextObj) {
    // Recursively extract text content from Shopify rich text JSON structure
    let text = '';
    
    if (!richTextObj) return '';
    
    // If it's a text node, return its value
    if (richTextObj.type === 'text' && richTextObj.value) {
      return richTextObj.value;
    }
    
    // If it has children, recursively process them
    if (richTextObj.children && Array.isArray(richTextObj.children)) {
      richTextObj.children.forEach((child, index) => {
        const childText = this.extractRichTextContent(child);
        if (childText) {
          text += childText;
          
          // Add line break after paragraphs (except the last one)
          if (child.type === 'paragraph' && index < richTextObj.children.length - 1) {
            text += '\n';
          }
        }
      });
    }
    
    return text;
  }

  extractSizes(product) {
    // Extract size options from variants
    const sizeOption = product.options.find(opt => 
      opt.name.toLowerCase() === 'size' || 
      opt.name.toLowerCase() === 'koko'
    );
    
    if (sizeOption) {
      return sizeOption.values;
    }
    
    return null;
  }

  async createPDF(data) {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Load custom fonts (HelveticaNeueLT for labels, InputMono for body text)
    await this.loadCustomFonts(doc);

    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 15;
    
    // Convert hex colors
    const headerColor = this.hexToRGB(this.config.header_color || 'FFE600');
    const footerColor = this.hexToRGB(this.config.footer_color || 'FFE600');

    // Header Section (30mm height)
    doc.setFillColor(headerColor.r, headerColor.g, headerColor.b);
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    // Primary logo — pushed to the very left edge to maximise name space
    const logoEdgeMargin = 5;
    const maxLogoWidth = 42;
    const maxLogoHeight = 26;

    console.log('Logo URL in config:', this.config.logo_url);
    if (this.config.logo_url) {
      try {
        const logoUrl = decodeURIComponent(this.config.logo_url);
        console.log('Attempting to load logo...');
        const logoResult = await this.loadImage(logoUrl);
        console.log('Logo loaded successfully:', logoResult.width, 'x', logoResult.height);

        let logoWidth = maxLogoWidth;
        let logoHeight = maxLogoHeight;
        if (logoResult.width && logoResult.height) {
          const aspectRatio = logoResult.width / logoResult.height;
          if (aspectRatio > 1) {
            logoWidth = maxLogoWidth;
            logoHeight = maxLogoWidth / aspectRatio;
          } else {
            logoHeight = maxLogoHeight;
            logoWidth = maxLogoHeight * aspectRatio;
          }
        }

        const logoX = (pageWidth - logoWidth) / 2;  // centred in header
        const logoY = (30 - logoHeight) / 2;
        console.log('Logo dimensions:', logoWidth, 'x', logoHeight, 'at', logoX, logoY);
        doc.addImage(logoResult.data, 'PNG', logoX, logoY, logoWidth, logoHeight);
        console.log('✓ Logo added to PDF header successfully');
      } catch (error) {
        console.error('✗ Logo loading failed:', error.message);
      }
    }

    // Secondary logo — square badge, pushed to the very right edge
    const secLogoSize = 24; // square bounding box (DFF badge is 1:1)
    const secLogoEdgeMargin = 4;
    const placeholderWidth = secLogoSize;
    const placeholderHeight = secLogoSize;
    const placeholderX = pageWidth - secLogoEdgeMargin - secLogoSize;
    const placeholderY = (30 - secLogoSize) / 2;

    console.log('Secondary Logo URL in config:', this.config.secondary_logo_url);
    if (this.config.secondary_logo_url) {
      try {
        const secondaryLogoUrl = decodeURIComponent(this.config.secondary_logo_url);
        console.log('Attempting to load secondary logo...');
        const secondaryLogoResult = await this.loadImage(secondaryLogoUrl);
        console.log('Secondary logo loaded successfully:', secondaryLogoResult.width, 'x', secondaryLogoResult.height);

        let secondaryLogoWidth = secLogoSize;
        let secondaryLogoHeight = secLogoSize;
        if (secondaryLogoResult.width && secondaryLogoResult.height) {
          const aspectRatio = secondaryLogoResult.width / secondaryLogoResult.height;
          if (aspectRatio > 1) {
            secondaryLogoWidth = secLogoSize;
            secondaryLogoHeight = secLogoSize / aspectRatio;
          } else {
            secondaryLogoHeight = secLogoSize;
            secondaryLogoWidth = secLogoSize * aspectRatio;
          }
        }

        const secondaryLogoX = placeholderX + (secLogoSize - secondaryLogoWidth) / 2;
        const secondaryLogoY = placeholderY + (secLogoSize - secondaryLogoHeight) / 2;
        console.log('Secondary logo dimensions:', secondaryLogoWidth, 'x', secondaryLogoHeight, 'at', secondaryLogoX, secondaryLogoY);
        doc.addImage(secondaryLogoResult.data, 'PNG', secondaryLogoX, secondaryLogoY, secondaryLogoWidth, secondaryLogoHeight);
        console.log('✓ Secondary logo added to PDF header successfully');
      } catch (error) {
        console.error('✗ Secondary logo loading failed:', error.message);
      }
    }

    // Product name + SKU — top-left of white canvas (below header)
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(22);
    this.setLabelFont(doc);
    const titleMaxWidth = pageWidth - margin * 2;
    const titleLines = doc.splitTextToSize(data.name.toUpperCase(), titleMaxWidth);
    const titleLineHeight = 8;
    const titleStartY = 42;
    titleLines.forEach((line, index) => {
      doc.text(line, margin, titleStartY + index * titleLineHeight);
    });
    const titleBottomY = titleStartY + (titleLines.length - 1) * titleLineHeight;

    doc.setFontSize(11);
    this.setBodyFont(doc);
    const skuY = titleBottomY + 7;
    doc.text(data.sku, margin, skuY);

    // Product Image Section - FULL HEIGHT on RIGHT side
    let yPosition = skuY + 8;  // start below title + SKU
    const bulletPointsStartY = yPosition;
    const bulletPoints = data.middleSection;
    
    // Define layout areas
    const bulletPointsWidth = 70; // Left side for bullets
    const imageAreaX = margin + bulletPointsWidth + 10; // Right side for image
    const imageMaxWidth = pageWidth - imageAreaX - margin; // Remaining width for image
    
    // Calculate max height - use full white area (from top to footer)
    const footerStartY = pageHeight - 100;
    const imageMaxHeight = footerStartY - yPosition - 10; // Full height minus small margin
    
    let imageBottomY = yPosition; // Track where image ends
    
    // Render PRODUCT IMAGE on RIGHT side - FULL HEIGHT
    if (data.image) {
      try {
        const imgResult = await this.loadImage(data.image);
        
        // Calculate dimensions maintaining aspect ratio - USE FULL HEIGHT
        let imgWidth = imageMaxWidth;
        let imgHeight = imageMaxHeight;
        
        if (imgResult.width && imgResult.height) {
          const aspectRatio = imgResult.width / imgResult.height;
          
          if (aspectRatio > 1) {
            // Landscape: width is larger
            imgWidth = imageMaxWidth;
            imgHeight = imageMaxWidth / aspectRatio;
            if (imgHeight > imageMaxHeight) {
              imgHeight = imageMaxHeight;
              imgWidth = imageMaxHeight * aspectRatio;
            }
          } else {
            // Portrait or square: height is larger or equal
            imgHeight = imageMaxHeight;
            imgWidth = imageMaxHeight * aspectRatio;
            if (imgWidth > imageMaxWidth) {
              imgWidth = imageMaxWidth;
              imgHeight = imageMaxWidth / aspectRatio;
            }
          }
        }
        
        // Position image to the right side
        const imgX = imageAreaX;
        const imgY = yPosition;
        
        doc.addImage(imgResult.data, 'PNG', imgX, imgY, imgWidth, imgHeight);
        
        // Add callouts if available
        if (data.callouts && Array.isArray(data.callouts)) {
          this.addCallouts(doc, data.callouts, imgX, imgY, imgWidth, imgHeight);
        }
        
        imageBottomY = imgY + imgHeight;
        yPosition = imageBottomY + 15;
      } catch (error) {
        console.warn('Could not load product image:', error);
        yPosition += 20;
      }
    }
    
    // Render BULLET POINTS on LEFT side - START FROM BOTTOM
    if (bulletPoints) {
      console.log('Rendering bullet points on left side - from bottom to top');
      
      let bulletText = bulletPoints;
      if (Array.isArray(bulletText)) {
        bulletText = bulletText.join('\n');
      } else {
        bulletText = String(bulletText);
      }
      
      doc.setFontSize(11);
      this.setBodyFont(doc);
      doc.setTextColor(0, 0, 0);
      
      const lines = bulletText.split('\n').filter(line => line.trim() !== '');
      const footerStartY = pageHeight - 100;
      const maxBulletY = footerStartY - 10;
      
      const bulletIndent = margin + 3;
      const maxLineWidth = bulletPointsWidth - 5;
      
      // Calculate total height needed for bullets
      let totalBulletHeight = 0;
      const wrappedLinesArray = [];
      lines.forEach(line => {
        const wrappedLines = this.wrapText(doc, line.trim(), maxLineWidth);
        wrappedLinesArray.push(wrappedLines);
        totalBulletHeight += wrappedLines.length * 5.5;
      });
      
      // Start bullets from bottom (just above footer), going upward
      // We need to position so the LAST line is near maxBulletY
      let bulletY = maxBulletY - totalBulletHeight;
      
      // Make sure we don't start too high (above the image area)
      if (bulletY < bulletPointsStartY) {
        bulletY = bulletPointsStartY;
      }
      
      console.log(`Starting bullets at Y=${bulletY}, will end at Y=${bulletY + totalBulletHeight}, max=${maxBulletY}`);
      
      wrappedLinesArray.forEach((wrappedLines, lineIndex) => {
        if (bulletY > maxBulletY) {
          console.warn('Bullet points truncated - reached footer');
          return;
        }
        
        // Check if the first wrapped line already starts with a bullet
        const firstLine = wrappedLines[0] || '';
        const hasBullet = firstLine.trim().startsWith('•');
        
        // Only add bullet if text doesn't already have one
        if (!hasBullet) {
          doc.text('•', margin, bulletY);
        }
        
        wrappedLines.forEach((wrappedLine, index) => {
          if (bulletY < maxBulletY) {
            // If text already has bullet, render at margin; otherwise indent
            const xPos = (hasBullet && index === 0) ? margin : bulletIndent;
            doc.text(wrappedLine, xPos, bulletY);
            bulletY += 5.5;
          }
        });
      });
    }

    // Footer Section - starts at bottom (100mm height)
    // footerStartY already declared above for image calculation
    doc.setFillColor(footerColor.r, footerColor.g, footerColor.b);
    doc.rect(0, footerStartY, pageWidth, 100, 'F');
    
    // Reserve space for RETAILER PRICE STICKER on the right (no border)
    const stickerWidth = 45;
    const stickerHeight = 35;
    const stickerX = pageWidth - margin - stickerWidth;
    const stickerY = pageHeight - margin - stickerHeight;
    // Space is reserved but no visual rectangle or text drawn

    // Footer content
    doc.setTextColor(0, 0, 0);
    let footerY = footerStartY + 10;

    console.log('Rendering PDF footer with:', {
      customFields: data.customFields ? data.customFields.length : 0,
      sizes: data.sizes ? 'yes' : 'NO'
    });

    // Render sizes first if enabled (full width at top)
    if (data.sizes) {
      const sizesLabel = this.config.sizes_label || 'Sizes';

      doc.setFontSize(9);
      this.setLabelFont(doc);
      const labelWidth = doc.getTextWidth(sizesLabel + ': ');
      doc.text(sizesLabel + ':', margin, footerY);

      this.setBodyFont(doc);
      doc.setFontSize(9);
      doc.text(data.sizes.join(', '), margin + labelWidth + 2, footerY);

      footerY += 6;
    }

    // Render dynamic custom fields in up to 3 columns.
    // Column count scales with field count: 1 field → 1 col, 2 → 2 cols, 3+ → 3 cols.
    // Fields are assigned round-robin (field 0 → col 0, field 1 → col 1, …).
    // Available width excludes the sticker reservation on the right.
    const colGap = 4; // mm between columns
    const availableFooterWidth = pageWidth - (2 * margin); // full width, 3 equal columns

    if (data.customFields && data.customFields.length > 0) {
      const totalFields = data.customFields.length;
      const numColumns = Math.min(totalFields, 3);
      const columnWidth = (availableFooterWidth - (numColumns - 1) * colGap) / numColumns;

      // Build per-column X positions and Y trackers
      const colX = Array.from({ length: numColumns }, (_, i) => margin + i * (columnWidth + colGap));
      const colY = Array(numColumns).fill(footerY);

      console.log(`Rendering fields in ${numColumns} column(s), columnWidth=${columnWidth.toFixed(1)}mm`);

      const headingFontSize = 9;
      const bodyFontSize = 8;
      const lineHeight = 3.8;
      const bulletIndent = 3;
      const textWrapWidth = columnWidth - bulletIndent - 1;

      data.customFields.forEach((field, index) => {
        const col = index % numColumns;
        const columnX = colX[col];
        let currentY = colY[col];

        console.log(`Field ${index + 1} (${field.label}): col ${col + 1} at Y=${currentY.toFixed(1)}`);

        if (currentY > pageHeight - 8) {
          console.warn(`Skipping field "${field.label}" — no space left in col ${col + 1} (Y=${currentY.toFixed(1)}, max=${pageHeight - 8})`);
          return;
        }

        // Render field label — wrapped to column width so it never bleeds into adjacent columns
        doc.setFontSize(headingFontSize);
        this.setLabelFont(doc);
        const labelLines = this.wrapText(doc, field.label + ':', columnWidth);
        labelLines.forEach(line => {
          if (currentY < pageHeight - 5) {
            doc.text(line, columnX, currentY);
            currentY += headingFontSize * 0.45;
          }
        });
        currentY += 1.5;

        // Render field data
        doc.setFontSize(bodyFontSize);
        this.setBodyFont(doc);

        let fieldText = field.data;
        if (Array.isArray(fieldText)) {
          fieldText = fieldText.join('\n');
        } else {
          fieldText = String(fieldText);
        }

        const dataLines = fieldText.split('\n').filter(line => line.trim() !== '');

        dataLines.forEach(line => {
          if (currentY >= pageHeight - 5) return;

          const trimmedLine = line.trim();
          const hasBullet = trimmedLine.startsWith('•');
          const wrappedLines = this.wrapText(doc, trimmedLine, textWrapWidth);

          wrappedLines.forEach((wrappedLine, wrapIndex) => {
            if (currentY < pageHeight - 5) {
              if (wrapIndex === 0 && !hasBullet) {
                doc.text('•', columnX, currentY);
                doc.text(wrappedLine, columnX + bulletIndent, currentY);
              } else if (wrapIndex === 0 && hasBullet) {
                doc.text(wrappedLine, columnX, currentY);
              } else {
                doc.text(wrappedLine, columnX + bulletIndent, currentY);
              }
              currentY += lineHeight;
            }
          });
        });

        // Spacing after field
        currentY += 2;

        // Update this column's Y tracker
        colY[col] = currentY;
      });
    }

    // Save PDF
    const filename = `${data.name.replace(/[^a-z0-9]/gi, '_')}_datasheet.pdf`;
    doc.save(filename);
  }

  addCallouts(doc, callouts, imgX, imgY, imgWidth, imgHeight) {
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    
    callouts.forEach((callout, index) => {
      if (callout.text && callout.x && callout.y) {
        const x = imgX + (imgWidth * callout.x);
        const y = imgY + (imgHeight * callout.y);
        
        // Draw line from image to label
        const labelX = callout.position === 'right' ? imgX + imgWidth + 5 : imgX - 5;
        const labelY = y;
        
        doc.setDrawColor(0, 0, 0);
        doc.line(x, y, labelX, labelY);
        
        // Draw text
        const textAlign = callout.position === 'right' ? 'left' : 'right';
        doc.text(callout.text, labelX + (textAlign === 'left' ? 2 : -2), labelY, { align: textAlign });
      }
    });
  }

  loadImage(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      // Use Shopify image CDN and ensure HTTPS
      let cdnUrl = imageUrl;
      if (!cdnUrl.includes('cdn.shopify.com')) {
        cdnUrl = cdnUrl.replace(/^\/\//, 'https://');
      }
      if (!cdnUrl.startsWith('http')) {
        cdnUrl = 'https:' + cdnUrl;
      }
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          // Draw the image (preserve transparency for PNG)
          ctx.drawImage(img, 0, 0);
          
          // Return as PNG to preserve transparency
          resolve({
            data: canvas.toDataURL('image/png'),
            width: img.width,
            height: img.height
          });
        } catch (error) {
          console.error('Canvas error:', error);
          reject(new Error('Failed to process image'));
        }
      };
      
      img.onerror = (error) => {
        console.error('Image load error:', error);
        reject(new Error('Failed to load image'));
      };
      
      // Set src after event handlers are attached
      img.src = cdnUrl;
    });
  }

  async loadCustomFonts(doc) {
    this.customFontsLoaded = false;

    // Font data is embedded in product-pdf-fonts.js to avoid Shopify CDN
    // automatically converting uploaded TTF files to WOFF2 (which jsPDF cannot use).
    const fontData = window.PDF_FONT_DATA;
    if (!fontData) {
      console.warn('✗ PDF_FONT_DATA not found — product-pdf-fonts.js may not be loaded');
      return;
    }

    const fontConfigs = [
      {
        base64: fontData.helveticaLT93,
        filename: 'Helvetica_LT_93_Black_Extended.ttf',
        fontName: 'HelveticaNeueLT',
        fontStyle: 'normal'
      },
      {
        base64: fontData.inputMono,
        filename: 'InputMono-Regular.ttf',
        fontName: 'InputMono',
        fontStyle: 'normal'
      }
    ];

    try {
      // Validate fonts in a throwaway jsPDF document before committing to the real one.
      // getStringUnitWidth exercises getCharWidthsArray — the exact code path that fails
      // when font metrics are incomplete (e.g. WOFF/WOFF2 loaded as TTF).
      const { jsPDF } = window.jspdf;
      const testDoc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      for (const font of fontConfigs) {
        testDoc.addFileToVFS(font.filename, font.base64);
        testDoc.addFont(font.filename, font.fontName, font.fontStyle);
        testDoc.setFont(font.fontName, font.fontStyle);
        testDoc.setFontSize(12);
        testDoc.getStringUnitWidth('validation test string');
      }

      // Validation passed — register fonts in the real document
      for (const font of fontConfigs) {
        doc.addFileToVFS(font.filename, font.base64);
        doc.addFont(font.filename, font.fontName, font.fontStyle);
      }
      this.customFontsLoaded = true;
      console.log('✓ Custom fonts loaded and validated');
    } catch (error) {
      console.warn('✗ Custom fonts unavailable, using built-in Helvetica:', error.message);
      this.customFontsLoaded = false;
    }
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  setLabelFont(doc) {
    if (this.customFontsLoaded) {
      doc.setFont('HelveticaNeueLT', 'normal');
    } else {
      doc.setFont('helvetica', 'bold');
    }
  }

  setBodyFont(doc) {
    if (this.customFontsLoaded) {
      doc.setFont('InputMono', 'normal');
    } else {
      doc.setFont('helvetica', 'normal');
    }
  }

  safeDecodeLabel(str) {
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  }

  hexToRGB(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 230, b: 0 };
  }

  wrapText(doc, text, maxWidth) {
    // Ensure text is a string
    if (typeof text !== 'string') {
      text = Array.isArray(text) ? text.join(', ') : String(text);
    }
    
    const lines = [];
    const paragraphs = text.split('\n');
    
    paragraphs.forEach(paragraph => {
      const words = paragraph.split(' ');
      let currentLine = '';
      
      words.forEach(word => {
        const testLine = currentLine + word + ' ';
        const textWidth = doc.getTextWidth(testLine);
        
        if (textWidth > maxWidth && currentLine !== '') {
          lines.push(currentLine.trim());
          currentLine = word + ' ';
        } else {
          currentLine = testLine;
        }
      });
      
      if (currentLine.trim() !== '') {
        lines.push(currentLine.trim());
      }
    });
    
    return lines;
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.loadingState.hidden = !isLoading;
  }

  showMessage(text, type = 'success') {
    // Remove existing messages
    const existingMessage = this.container.querySelector('.pdf-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create new message
    const message = document.createElement('div');
    message.className = `pdf-message pdf-message--${type}`;
    message.textContent = text;
    this.container.appendChild(message);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      message.style.animation = 'pdf-fadeOut 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 5000);
  }
}

// Initialize PDF generators when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPDFGenerators);
} else {
  initPDFGenerators();
}

function initPDFGenerators() {
  const buttons = document.querySelectorAll('[data-product-pdf-button]');
  buttons.forEach(button => new ProductPDFGenerator(button));
}

// Re-initialize on section render (for theme editor)
document.addEventListener('shopify:section:load', initPDFGenerators);
